// src/pages/BrainAgeHistoryPage.js
import React, { useEffect, useState } from "react";
import {Link, useNavigate} from "react-router-dom";
import {useAuth} from "../contexts/authContext";
import { fetchWithAuth } from "../utils/fetchWithAuth";
import { Line } from "react-chartjs-2";
import { Chart, LineElement, PointElement, LinearScale, CategoryScale } from "chart.js";

Chart.register(LineElement, PointElement, LinearScale, CategoryScale);

const BrainAgeHistoryPage = () => {
  const [history, setHistory] = useState([]);
  const navigate = useNavigate();
  const [userNickname, setUserNickname] = useState('');
  const {isLoggedIn, setIsLoggedIn, userEmail, setUserEmail} = useAuth();
  const [loading, setLoading] = useState(true);

  const chartData = {
    labels: history.map(item => item.date),
    datasets: [
      {
        label: "🧠 뇌 나이",
        data: history.map(item => item.brain_age),
        borderColor: "rgba(75,192,192,1)",
        tension: 0.3,
      },
    ],
  };


  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      alert("로그인이 필요합니다.");
      navigate("/login");
      return;
    }

    fetchWithAuth(`/brain-trainer/brain-age-history`, {})
        .then(res => {
          if (!res.ok) throw new Error("서버 응답 오류");
          return res.json();
        })
        .then(data => {
          if (Array.isArray(data)) {
            setHistory(data);
          }
        })
        .catch(err => {
          console.error("히스토리 불러오기 오류:", err);
          alert("데이터를 불러오는 중 오류가 발생했습니다.");
        })
        .finally(() => {
          setLoading(false);
        });
  }, []);

  useEffect(() => {
    const nickname = localStorage.getItem("nickname");
    if (nickname) setUserNickname(nickname);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("email");
    localStorage.removeItem("nickname");
    localStorage.removeItem("usage_count");
    localStorage.removeItem("daily_limit");
    setIsLoggedIn(false);
    setUserEmail("");
    alert("로그아웃 되었습니다.");
    navigate("/login");
  };

  return (
      <div className="min-h-screen bg-gray-100 dark:bg-gray-900 text-white">
        <header
            className="w-full flex flex-col md:flex-row justify-between items-center px-4 py-4 border-b bg-white dark:bg-gray-800">
          <div className="text-center md:text-left mb-4 md:mb-0">
            <h1 className="text-xl font-bold text-blue-600 dark:text-blue-300">두뇌트레이너</h1>
            <nav className="mt-2 text-sm">
              <Link to="/" className="hover:text-blue-400">메인페이지로 이동</Link>
              <span className="mx-2">|</span>
              <Link to="/brain-trainer" className="hover:text-blue-400">두뇌트레이너(Brain Trainer)</Link>
              <span className="mx-2">|</span>
            </nav>
          </div>

          <div className="flex flex-col items-end gap-2 mt-4 md:mt-0">
            {isLoggedIn && (
                <div className="text-sm font-semibold text-green-300 text-center md:text-right w-full">
                  어서오세요, <span className="text-yellow-400">{userNickname}</span> 님
                </div>
            )}
            <div className="flex gap-2">
              {isLoggedIn ? (
                  <button
                      onClick={handleLogout}
                      className="px-4 py-2 text-sm font-semibold bg-red-500 text-white rounded-full">
                    Sign out
                  </button>
              ) : (
                  <Link to="/login" className="px-4 py-2 text-sm font-semibold bg-blue-500 text-white rounded-full">
                    Sign in
                  </Link>
              )}
              {!isLoggedIn && (
                  <Link to="/signup" className="px-4 py-2 text-sm font-semibold text-white bg-green-500 rounded-full">
                    Sign up
                  </Link>
              )}
              {isLoggedIn && (
                  <Link to="/mypage" className="px-4 py-2 text-sm font-semibold text-white bg-green-500 rounded-full">
                    MyPage
                  </Link>
              )}
            </div>
          </div>
        </header>

        <div className="max-w-2xl mx-auto mt-8 p-4 dark:bg-gray-900 rounded-xl shadow">
          <h2 className="text-2xl font-bold mb-4 text-center">🧠 나의 뇌 나이 히스토리</h2>

          {loading ? (
              <p className="text-center text-gray-400">불러오는 중...</p>
          ) : history.length === 0 ? (
              <p className="text-center text-gray-500">저장된 기록이 없습니다.</p>
          ) : (
              <>
                {/* 그래프 삽입 */}
                {history.length > 1 && (
                    <div className="mb-6">
                      <h3 className="text-lg font-semibold text-center mb-2 text-white">📊 뇌 나이 변화 그래프</h3>
                      <Line
                          data={{
                            labels: history.map(item => item.date),
                            datasets: [
                              {
                                label: "🧠 뇌 나이",
                                data: history.map(item => item.brain_age),
                                borderColor: "rgba(75,192,192,1)",
                                tension: 0.3,
                              },
                            ],
                          }}
                      />
                    </div>
                )}

                {/* 리스트 출력 */}
                <ul className="space-y-3">
                  {history.map((item, idx) => (
                      <li key={idx} className="flex justify-between items-center p-3 bg-gray-300 rounded-lg">
                        <div>
                          <p className="font-semibold text-gray-600">테스트 날짜 : {item.date}</p>
                          <p className="text-sm text-gray-600">정확도: {(item.accuracy * 100).toFixed(1)}%</p>
                        </div>
                        <span className="text-xl font-bold text-blue-600">{item.brain_age}세</span>
                      </li>
                  ))}
                </ul>
              </>
          )}
        </div>
      </div>
  );
}

export default BrainAgeHistoryPage;
